import { isNativePlatform } from './native/platform';
import { supabase } from './supabase';

// Web Push subscription lifecycle. Per plan §3 / Sprint 3 deliverables:
// in-browser push via the PWA service worker on desktop + Android. iOS
// falls back to SMS for time-critical items (iOS web push requires 16.4+
// installed-PWA which is unreliable for a parent population).
//
// VAPID public key is delivered via VITE_VAPID_PUBLIC_KEY at build time.
// The matching private key lives in Supabase Edge Function secrets and is
// used by the send-push function to sign requests.

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushIsSupported(): boolean {
  // Native (Capacitor) apps do not use the Web Push / service-worker path —
  // delivery there needs FCM / APNs, a tracked follow-up. Until that lands,
  // report unsupported on native so the notifications UI shows a graceful
  // state instead of calling Web Push APIs that will not deliver.
  return (
    typeof window !== 'undefined' &&
    !isNativePlatform() &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export type PushPermission = 'default' | 'granted' | 'denied' | 'unsupported';

export function getPushPermission(): PushPermission {
  if (!pushIsSupported()) return 'unsupported';
  return Notification.permission;
}

export async function ensurePushSubscription(
  vapidPublicKey: string,
): Promise<PushSubscription | null> {
  if (!pushIsSupported()) return null;
  if (!vapidPublicKey) {
    // eslint-disable-next-line no-console
    console.warn('[push] VAPID public key not configured; push disabled');
    return null;
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  sub ??= await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  });
  return sub;
}

export async function registerPushSubscriptionWithServer(sub: PushSubscription): Promise<void> {
  const json = sub.toJSON();
  const userResp = await supabase.auth.getUser();
  const userId = userResp.data.user?.id;
  if (!userId) return;

  await supabase.from('push_subscriptions').upsert(
    {
      user_id: userId,
      endpoint: sub.endpoint,
      p256dh: json.keys?.p256dh ?? '',
      auth: json.keys?.auth ?? '',
      user_agent: navigator.userAgent,
      last_used_at: new Date().toISOString(),
    },
    { onConflict: 'endpoint' },
  );
}

export async function unregisterPushSubscription(): Promise<void> {
  if (!pushIsSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  await sub.unsubscribe();
  await supabase.from('push_subscriptions').delete().eq('endpoint', sub.endpoint);
}
